import React from 'react';
import PropTypes from 'prop-types';
import { OPTIN_FORMATS, OPTIN_RADIO_DEFAULTS } from 'utils/constants';
import InputContainer from '../../InputContainer';
import SectionNumber from '../../SectionNumber';
import TextAreaAttribute from '../TextAreaAttribute';
import RadioButtonAttribute from '../RadioButtonAttribute';
import RadioGroupContainer from '../../RadioGroupContainer';

export default function FieldEditorOptInOverride({
  keyPath,
  uiState = {},
  values = {},
  onFieldValueChange,
  onFieldUIStateChange,
  disabled = false,
}) {

  const defaultCheckedPath = [...keyPath, 'optInData', 'defaultChecked'];
  const optInData = values?.overrides?.gdpr?.editors?.optInData || {};
  const formatValue = optInData.format || OPTIN_FORMATS.RADIO;
  const defaultRadioValue = optInData.defaultRadio || OPTIN_RADIO_DEFAULTS.NONE;
  const defaultCheckedValue = optInData.defaultChecked ?? false;
  const formatBoxDisabled = disabled === true;

  const onDefaultCheckedChange = (changeKeyPath, value) => {
    onFieldValueChange(changeKeyPath, (value === 'checked'));
  };

  return (
    <div className="row mx-0 border-top border-secondary pb-3">
      <div className="col-1 p-0">
        <SectionNumber number="2B" />
      </div>
      <div className="col-11">
        <div className="row">
          <div className="col-12 pt-3 pl-3">
            <div className="row mb-4">
              <div className="col-sm-6 mt-3">
                <p className="mb-0 font-weight-bold">Implied Consent Overrides</p>
                <div className="mb-0 mt-2">
                  <p>
                    When a prospect&apos;s location is within a GDPR region, the field will be presented as Yes/No radio buttons.
                  </p>
                </div>
              </div>
              <div className="col-sm-6 mt-3">
                <InputContainer
                  label="Override Display Language (optional)"
                  keyPath={[...keyPath, 'optInData', 'disclaimer']}
                  uiState={uiState?.overrides?.gdpr?.editors?.optInData?.disclaimer}
                  disabled={disabled}
                >
                  <TextAreaAttribute
                    keyPath={[...keyPath, 'optInData', 'disclaimer']}
                    value={optInData.disclaimer || ''}
                    disabled={disabled}
                    onFieldValueChange={onFieldValueChange}
                    onFieldUIStateChange={onFieldUIStateChange}
                  />
                </InputContainer>
              </div>
            </div>

            <div className="mb-3">
              <InputContainer
                label="Override Prospect Share Opt-In Language (optional)"
                keyPath={[...keyPath, 'optInData', 'checkboxText']}
                uiState={uiState?.overrides?.gdpr?.editors?.optInData?.checkboxText}
                disabled={disabled}
              >
                <TextAreaAttribute
                  keyPath={[...keyPath, 'optInData', 'checkboxText']}
                  value={optInData.checkboxText || ''}
                  disabled={disabled}
                  onFieldValueChange={onFieldValueChange}
                  onFieldUIStateChange={onFieldUIStateChange}
                />
              </InputContainer>
            </div>

            <div className="row mb-4">
              <div className="col-lg-6 mt-2 mb-4 mb-lg-0">
                <p className="font-weight-bold">Implied Consent Field Type *</p>
                <div className="row ml-0 mr-4 border-left border-secondary">
                  <div className="col-sm-6 py-3 border border-secondary border-left-0">
                    <RadioButtonAttribute
                      disabled={formatBoxDisabled}
                      field={OPTIN_FORMATS.CHECKBOX}
                      keyPath={[...keyPath, 'optInData', 'format']}
                      label="Checkbox"
                      onRadioButtonChange={onFieldValueChange}
                      radioGroupName="Default value"
                      value={formatValue === OPTIN_FORMATS.CHECKBOX}
                      omitBorder
                      stacked
                    />
                    <div className="mx-4 my-2">
                      <RadioGroupContainer
                        disabled={formatBoxDisabled || formatValue !== OPTIN_FORMATS.CHECKBOX}
                        radioGroupName="Default value"
                        stacked
                        uiState={uiState}
                      >
                        <RadioButtonAttribute
                          disabled={formatBoxDisabled || formatValue !== OPTIN_FORMATS.CHECKBOX}
                          field="checked"
                          keyPath={defaultCheckedPath}
                          label="Checked"
                          onRadioButtonChange={onDefaultCheckedChange}
                          radioGroupName="Default value"
                          value={defaultCheckedValue === true}
                          omitBorder
                          stacked
                        />

                        <RadioButtonAttribute
                          disabled={formatBoxDisabled || formatValue !== OPTIN_FORMATS.CHECKBOX}
                          field="unchecked"
                          keyPath={defaultCheckedPath}
                          label="Unchecked"
                          onRadioButtonChange={onDefaultCheckedChange}
                          radioGroupName="Default value"
                          value={defaultCheckedValue === false}
                          omitBorder
                          stacked
                        />
                      </RadioGroupContainer>
                    </div>
                  </div>
                  <div className="col-sm-6 py-3 border border-secondary border-left-0">
                    <RadioButtonAttribute
                      disabled
                      field={OPTIN_FORMATS.RADIO}
                      keyPath={[...keyPath, 'optInData', 'format']}
                      label="Radio"
                      onRadioButtonChange={onFieldValueChange}
                      radioGroupName="Default value"
                      value={formatValue === OPTIN_FORMATS.RADIO}
                      omitBorder
                      stacked
                    />
                    <div className="mx-4 my-2">
                      <RadioGroupContainer
                        disabled
                        radioGroupName="Default value"
                        stacked
                        uiState={uiState}
                      >
                        <RadioButtonAttribute
                          disabled
                          field={OPTIN_RADIO_DEFAULTS.NONE}
                          keyPath={[...keyPath, 'optInData', 'defaultRadio']}
                          label="None"
                          onRadioButtonChange={onFieldValueChange}
                          radioGroupName="Default value"
                          value={defaultRadioValue === OPTIN_RADIO_DEFAULTS.NONE}
                          omitBorder
                          stacked
                        />
                        <RadioButtonAttribute
                          disabled
                          field={OPTIN_RADIO_DEFAULTS.YES}
                          keyPath={[...keyPath, 'optInData', 'defaultRadio']}
                          label="Yes"
                          onRadioButtonChange={onFieldValueChange}
                          radioGroupName="Default value"
                          value={defaultRadioValue === OPTIN_RADIO_DEFAULTS.YES}
                          omitBorder
                          stacked
                        />
                        <RadioButtonAttribute
                          disabled
                          field={OPTIN_RADIO_DEFAULTS.NO}
                          keyPath={[...keyPath, 'optInData', 'defaultRadio']}
                          label="No"
                          onRadioButtonChange={onFieldValueChange}
                          radioGroupName="Default value"
                          value={defaultRadioValue === OPTIN_RADIO_DEFAULTS.NO}
                          omitBorder
                          stacked
                        />
                      </RadioGroupContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

FieldEditorOptInOverride.propTypes = {
  keyPath: PropTypes.array.isRequired,
  uiState: PropTypes.object,
  values: PropTypes.object,
  onFieldValueChange: PropTypes.func.isRequired,
  onFieldUIStateChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
